import {Trans} from '@lingui/macro';
import React, {FC, memo, useCallback} from 'react';
import styled from 'styled-components';

import {Handler} from '../../../../../libs/shared/helpers/src/react/reactHelpers';
import {Button} from '../../../../../libs/shared/ui/src/components/buttons/button';
import {Icon} from '../../../../../libs/shared/ui/src/components/icon/icon';
import {VisualSizesEnum} from '../../../../../libs/shared/ui/src/styles/commonStyles';
import {fontSizes, fontWeights} from '../../../../../libs/shared/ui/src/styles/fontStyles';
import {useAppDispatch} from '../../../../helpers/reduxHelpers';
import {navigateToMessageTemplatesAction} from '../../../../store/stateless/navigationActions';

/*
 * Props.
 */

interface AnswerFolderDropdownInfoTooltipProps {
  onRequestClose?: Handler;
}

/*
 * Styles.
 */

const StyledInfoTooltipWrapperDiv = styled.div`
  display: flex;
  flex-direction: column;
  color: ${(p) => p.theme.greys.shade10};
  font-weight: ${fontWeights.normal};
  padding: 14px 10px;
  max-width: 220px;
`;

const StyledInfoTooltipHeaderDiv = styled.div`
  display: flex;
`;

const StyledDetailsSpan = styled.div`
  font-size: ${fontSizes.verySmall};
  font-weight: ${fontWeights.medium};
  line-height: 18px;
  padding: 10px 0px 16px;
`;

const StyledHeaderSpan = styled.span`
  font-size: ${fontSizes.medium};
  font-weight: ${fontWeights.bold};
  padding: 0px 8px;
`;

const StyledButtonWrapperDiv = styled.div`
  display: flex;
  align-items: flex-start;
`;

/*
 * Component.
 */

export const AnswerFolderDropdownInfoTooltip: FC<AnswerFolderDropdownInfoTooltipProps> = memo(
  ({onRequestClose}) => {
    const openSettings = useOpenSettingsCallback(onRequestClose);

    return (
      <StyledInfoTooltipWrapperDiv>
        <StyledInfoTooltipHeaderDiv>
          <Icon name="folderSmall" />
          <StyledHeaderSpan>
            <Trans id="answer-folder-dropdown-info-tooltip-header">Need a new folder?</Trans>
          </StyledHeaderSpan>
        </StyledInfoTooltipHeaderDiv>

        <StyledDetailsSpan>
          <Trans id="answer-folder-dropdown-info-tooltip-text">
            You can create new folders from your Settings.
          </Trans>
        </StyledDetailsSpan>

        <StyledButtonWrapperDiv>
          <Button size={VisualSizesEnum.SMALL} onClick={openSettings}>
            <Trans id="answer-folder-dropdown-info-tooltip-go-to-settings">Go to Settings</Trans>
          </Button>
        </StyledButtonWrapperDiv>
      </StyledInfoTooltipWrapperDiv>
    );
  },
);

const useOpenSettingsCallback = (onRequestClose?: Handler) => {
  const dispatch = useAppDispatch();
  return useCallback(() => {
    if (onRequestClose) {
      onRequestClose();
    }

    dispatch(navigateToMessageTemplatesAction());
  }, [dispatch, onRequestClose]);
};
